'use client';
import React, { useState, useEffect } from 'react';
import { FileText, ChevronRight, Download } from 'lucide-react';

interface AuditEntry {
  id: string;
  actor: string;
  actorType: 'control' | 'developer' | 'owner' | 'system';
  action: string;
  target: string;
  targetType: string;
  result: 'success' | 'denied' | 'error';
  timestamp: string;
  ip: string;
  org?: string;
}

const actorTypeBadge = (type: string) => {
  const map: Record<string, string> = {
    control: 'badge-danger',
    developer: 'badge-info',
    owner: 'badge-primary',
    system: 'badge-neutral',
  };
  return map[type] || 'badge-neutral';
};

const resultBadge = (result: string) => {
  if (result === 'success') return 'badge-success';
  if (result === 'denied') return 'badge-warning';
  return 'badge-danger';
};

export default function AuditLogFeed() {
  const [filter, setFilter] = useState<'all' | 'denied' | 'control' | 'developer'>('all');
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/audit-logs?filter=${filter}&limit=50`);
        if (res.ok) {
          const data = await res.json();
          setEntries(data.logs || []);
        }
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [filter]);

  const handleDownload = () => {
    const csv = [
      ['ID', 'Actor', 'Actor Type', 'Action', 'Target', 'Target Type', 'Result', 'Timestamp', 'IP'],
      ...entries.map(e => [e.id, e.actor, e.actorType, e.action, e.target, e.targetType, e.result, e.timestamp, e.ip]),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-primary" />
          <h3 className="text-sm font-600 text-foreground">Recent Audit Log</h3>
          <span className="badge-neutral text-[11px] font-500 px-2 py-0.5 rounded-full">{entries.length} entries</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {(['all', 'denied', 'control', 'developer'] as const).map((f) => (
              <button
                key={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`text-[11px] font-500 px-2.5 py-1 rounded-md transition-all duration-150 capitalize ${
                  filter === f ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            onClick={handleDownload}
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="Download CSV"
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="divide-y divide-border">
          {[...Array(5)].map((_, i) => (
            <div key={`skel-${i}`} className="px-5 py-3 animate-pulse">
              <div className="flex gap-3">
                <div className="w-14 h-5 bg-muted rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className={`flex items-start gap-3 px-5 py-3 hover:bg-muted/50 transition-colors
                ${entry.result === 'denied' || entry.result === 'error' ? 'bg-danger/2' : ''}
              `}
            >
              <div className="flex flex-col items-center gap-1 shrink-0 mt-0.5">
                <span className={`text-[10px] font-700 px-1.5 py-0.5 rounded capitalize ${actorTypeBadge(entry.actorType)}`}>
                  {entry.actorType}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12px] font-600 text-foreground truncate max-w-[160px]">{entry.actor}</span>
                  <span className="text-[11px] text-muted-foreground">→</span>
                  <span className="text-[12px] font-500 text-foreground font-mono truncate max-w-[180px]">{entry.action}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[11px] text-muted-foreground">Target:</span>
                  <span className="text-[11px] font-500 text-foreground font-mono">{entry.target}</span>
                  {entry.org && (
                    <>
                      <span className="text-[11px] text-muted-foreground">·</span>
                      <span className="text-[11px] text-muted-foreground truncate max-w-[160px]">{entry.org}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-muted-foreground font-mono">{entry.ip}</span>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{entry.timestamp}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-600 px-2 py-0.5 rounded uppercase ${resultBadge(entry.result)}`}>
                  {entry.result}
                </span>
                <button className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                  <ChevronRight size={12} />
                </button>
              </div>
            </div>
          ))}
          {entries.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">No audit entries found</div>
          )}
        </div>
      )}

      <div className="px-5 py-3 border-t border-border bg-muted/30 flex items-center justify-between">
        <span className="text-[12px] text-muted-foreground">Showing {entries.length} entries</span>
        <button className="text-[12px] font-500 text-primary hover:underline flex items-center gap-1">
          View full audit log <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}